/**
 * AmenitiesView — read mode for a property's assigned amenities
 * (SCRUM-141 / DS-010-5).
 *
 * Renders the server-grouped amenities as read-only pills grouped by category
 * (humanized label + graceful category glyph). A per-amenity note, when present,
 * shows as a caption under its pill. When there are no groups, a real
 * empty-state ("No amenities added yet") is shown with the Edit CTA.
 *
 * The Edit affordance is OPTIONAL (`onEdit`): the panel wraps this view in a
 * permission gate and only passes `onEdit` when the user may write, so a
 * read-only viewer never sees an Edit button. A raw `amenityId` UUID is NEVER
 * rendered — items are keyed on it internally but only the label + note reach
 * the DOM.
 */

import * as React from 'react';
import { Button, Icon } from '@sparta/ui-kit';
import type { components } from '@artemis/contracts';
import { humanizeCategory } from '../../api/amenities-mapper.js';
import { resolveCategoryIcon } from '../../api/amenity-icons.js';

type PropertyAmenityGroupDto = components['schemas']['PropertyAmenityGroupDto'];

export interface AmenitiesViewProps {
  readonly groups: readonly PropertyAmenityGroupDto[];
  readonly onEdit?: () => void;
}

function EditButton({ onEdit }: { readonly onEdit: () => void }): React.ReactElement {
  return (
    <Button variant="secondary" size="sm" icon="pencil" onClick={onEdit}>
      Edit amenities
    </Button>
  );
}

export function AmenitiesView({
  groups,
  onEdit,
}: AmenitiesViewProps): React.ReactElement {
  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-[var(--border-default)] bg-[var(--bg-sunken)] px-6 py-10 text-center">
        <span aria-hidden="true" className="inline-flex text-[color:var(--fg-tertiary)]">
          <Icon name="sparkles" size={24} />
        </span>
        <p className="m-0 text-sm text-[color:var(--fg-secondary)]">
          No amenities added yet.
        </p>
        {onEdit !== undefined ? <EditButton onEdit={onEdit} /> : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {onEdit !== undefined ? (
        <div className="flex justify-end">
          <EditButton onEdit={onEdit} />
        </div>
      ) : null}
      {groups.map((group) => (
        <section key={group.category} className="flex flex-col gap-3">
          <h3 className="m-0 flex items-center gap-2 text-sm font-semibold text-[color:var(--fg-primary)]">
            <span
              aria-hidden="true"
              className="inline-flex text-[color:var(--fg-tertiary)]"
            >
              <Icon name={resolveCategoryIcon(group.category)} size={18} />
            </span>
            {humanizeCategory(group.category)}
          </h3>
          <ul className="m-0 flex list-none flex-wrap gap-3 p-0">
            {group.amenities.map((amenity) => (
              <li
                key={amenity.amenityId}
                className="flex flex-col gap-0.5 rounded-lg border border-solid border-[var(--border-subtle)] bg-[var(--bg-surface)] px-3 py-1.5"
              >
                <span className="text-sm font-medium text-[color:var(--fg-primary)]">
                  {amenity.label}
                </span>
                {amenity.note !== null && amenity.note !== undefined ? (
                  <span className="text-xs text-[color:var(--fg-tertiary)]">
                    {amenity.note}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
